import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mocApi } from '../api/client';
import { fmtDate, StatusBadge, AlertBadge, CategoryBadge } from '../lib/mocUtils';
import { Card } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useSiteFilter } from '../lib/useSites';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Stack, FolderOpen, Warning, Timer, Fire, ShieldWarning, ListChecks,
  ClockCountdown, Hourglass, Archive, CheckSquare,
} from '@phosphor-icons/react';

function Kpi({ icon: Icon, label, value, tone, onClick, testid }) {
  const tones = {
    default: 'text-primary', red: 'text-red-600', amber: 'text-amber-600',
    teal: 'text-teal-600', violet: 'text-violet-600',
  };
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left bg-card border border-border rounded-sm p-4 hover:border-primary hover:shadow-sm transition-colors group">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</span>
        <Icon size={20} weight="bold" className={`${tones[tone] || tones.default} opacity-70 group-hover:opacity-100`} />
      </div>
      <div className="mt-3 font-display text-3xl font-black tracking-tight">{value}</div>
    </button>
  );
}

function deriveKpis(mocs) {
  const openS = (m) => !['cloturee', 'annulee'].includes(m.status);
  return {
    total: mocs.length,
    open: mocs.filter(openS).length,
    cat1: mocs.filter((m) => m.category === 1).length,
    cat2: mocs.filter((m) => m.category === 2).length,
    temporary: mocs.filter((m) => m.nature === 'Temporaire').length,
    urgent: mocs.filter((m) => m.nature === 'Urgent' || m.alert === 'critical').length,
    pssrPending: mocs.filter((m) => m.pssrRequired && openS(m)).length,
    actionsOpen: mocs.reduce((s, m) => s + (m.openActions || 0), 0),
    actionsLate: mocs.reduce((s, m) => s + (m.lateActions || 0), 0),
    avgDelayDays: mocs.length ? Math.round(mocs.reduce((s, m) => s + (m.ageDays || 0), 0) / mocs.length) : 0,
    oldMocs: mocs.filter((m) => (m.ageDays || 0) >= 60).length,
  };
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [allMocs, setAllMocs] = useState([]);
  const [validations, setValidations] = useState([]);
  const [sidCounts, setSidCounts] = useState({});
  const navigate = useNavigate();
  const { sites: adminSites, isNational, site: siteFilter, setSite: setSiteFilter } = useSiteFilter();

  useEffect(() => {
    mocApi.getDashboard().then(setData);
    mocApi.listMocs().then((m) => setAllMocs(m || []));
    mocApi.listValidations().then((v) => setValidations(v || [])).catch(() => {});
    mocApi.getSidCounts().then(setSidCounts);
  }, []);

  const siteActive = siteFilter !== 'all';
  const filteredMocs = useMemo(
    () => (siteActive ? allMocs.filter((m) => m.site === siteFilter) : allMocs),
    [allMocs, siteActive, siteFilter],
  );
  const k = siteActive ? deriveKpis(filteredMocs) : (data?.kpis || {});
  const byCategory = siteActive
    ? [1, 2, 3].map((c) => ({ name: `Cat. ${c}`, value: filteredMocs.filter((m) => m.category === c).length })).filter((x) => x.value > 0)
    : (data?.byCategory || []);
  const recent = useMemo(
    () => [...filteredMocs].sort((a, b) => ((b.updatedAt || '') > (a.updatedAt || '') ? 1 : -1)).slice(0, 5),
    [filteredMocs],
  );
  const pending = siteActive
    ? validations.filter((v) => filteredMocs.some((m) => m.id === v.id)).length
    : validations.length;

  const go = (params) => navigate(`/mocs?${new URLSearchParams(params).toString()}`);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Vue d'ensemble</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">Tableau de bord</h1>
        </div>
        {isNational && adminSites.length > 0 && (
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger data-testid="dashboard-filter-site" className="w-full sm:w-56 h-9">
              <SelectValue placeholder="Site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les sites</SelectItem>
              {adminSites.map((s) => (
                <SelectItem key={s.Site_ID} value={s.Site_ID}>{s.Nom_Site || s.Site_ID}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {!data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-24 bg-muted animate-pulse rounded-sm" />)}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <button data-testid="card-my-late-actions" onClick={() => navigate('/actions?filter=late')}
              className="text-left rounded-sm p-5 border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 hover:shadow-sm transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-red-700 dark:text-red-300">Mes actions en retard</span>
                <ClockCountdown size={22} weight="bold" className="text-red-600" />
              </div>
              <div className="mt-2 font-display text-4xl font-black text-red-700 dark:text-red-300">{k.actionsLate ?? 0}</div>
              <div className="text-xs text-red-600/80 mt-1">Cliquer pour voir la liste</div>
            </button>
            <button data-testid="card-pending-approvals" onClick={() => navigate('/validations')}
              className="text-left rounded-sm p-5 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 hover:shadow-sm transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">Approbations en attente</span>
                <CheckSquare size={22} weight="bold" className="text-amber-600" />
              </div>
              <div className="mt-2 font-display text-4xl font-black text-amber-700 dark:text-amber-300">{pending}</div>
              <div className="text-xs text-amber-600/80 mt-1">Vos décisions à prendre</div>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi testid="kpi-total" icon={Stack} label="Total MOC" value={k.total} onClick={() => go({})} />
            <Kpi testid="kpi-open" icon={FolderOpen} label="Ouvertes" value={k.open} tone="teal" onClick={() => go({ scope: 'open' })} />
            <Kpi testid="kpi-cat1" icon={Warning} label="Catégorie 1" value={k.cat1} tone="red" onClick={() => go({ category: '1' })} />
            <Kpi testid="kpi-cat2" icon={Warning} label="Catégorie 2" value={k.cat2} tone="amber" onClick={() => go({ category: '2' })} />
            <Kpi testid="kpi-temporary" icon={Timer} label="Temporaires" value={k.temporary} tone="violet" onClick={() => go({ nature: 'Temporaire' })} />
            <Kpi testid="kpi-urgent" icon={Fire} label="Urgents" value={k.urgent} tone="red" onClick={() => go({ nature: 'Urgent' })} />
            <Kpi testid="kpi-pssr" icon={ShieldWarning} label="PSSR en attente" value={k.pssrPending} tone="amber" onClick={() => navigate('/pssr')} />
            <Kpi testid="kpi-actions-open" icon={ListChecks} label="Actions ouvertes" value={k.actionsOpen} tone="teal" onClick={() => navigate('/actions')} />
            <Kpi testid="kpi-actions-late" icon={ClockCountdown} label="Actions en retard" value={k.actionsLate} tone="red" onClick={() => navigate('/actions?filter=late')} />
            <Kpi testid="kpi-avg-delay" icon={Hourglass} label="Délai moyen (j)" value={k.avgDelayDays} onClick={() => go({})} />
            <Kpi testid="kpi-old" icon={Archive} label="MOC anciennes" value={k.oldMocs} tone="violet" onClick={() => go({ scope: 'old' })} />
            <Kpi testid="kpi-sid" icon={ShieldWarning} label="Points SiD non conf." value={(siteActive ? filteredMocs : allMocs).reduce((s, m) => s + (sidCounts[m.id] || 0), 0)} tone="red" onClick={() => go({})} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="rounded-sm p-4 lg:col-span-2">
              <h3 className="font-display font-bold mb-4">Tendance créées / clôturées</h3>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214,32%,88%)" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="créées" stroke="hsl(217,60%,35%)" strokeWidth={2} />
                  <Line type="monotone" dataKey="clôturées" stroke="hsl(173,58%,39%)" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card className="rounded-sm p-4">
              <h3 className="font-display font-bold mb-4">Par catégorie</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={byCategory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214,32%,88%)" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="hsl(217,60%,35%)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card className="rounded-sm p-0 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-display font-bold">MOC récentes</h3>
              <button data-testid="view-all-mocs" onClick={() => go({})} className="text-sm font-semibold text-primary hover:underline">Voir tout</button>
            </div>
            <div className="divide-y divide-border">
              {recent.map((m) => (
                <button key={m.id} data-testid={`recent-moc-${m.id}`} onClick={() => navigate(`/moc/${m.id}`)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{m.id}</span>
                      <CategoryBadge category={m.category} />
                    </div>
                    <div className="font-semibold text-sm truncate mt-0.5">{m.title}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2"><AlertBadge level={m.alert} /><StatusBadge status={m.status} /></div>
                  <span className="text-xs text-muted-foreground hidden md:block">{fmtDate(m.updatedAt)}</span>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
